import api from './api';
import type { ApiResponse } from '@/types/api.types';
import type { BackupDestination } from '@/types/backup-destination.types';
import type { StoragePoolType } from '@/types/enums.generated';

export interface DestinationInfo {
  id: string;
  name: string;
  type: string;
  provider: string;
  status: string;
  priority: number;
  /** Backup-pool members only (ADR-052 D2): files this member alone can restore. */
  coverage?: { covered_files: number; total_files: number; as_of: string };
}

export interface StoragePool {
  id: string;
  name: string;
  type: StoragePoolType;
  description?: string;
  is_enabled: boolean;
  destinations: DestinationInfo[];
  created_at: string;
  updated_at: string;
}

export interface CreateStoragePoolRequest {
  name: string;
  type: StoragePoolType;
  description?: string;
}

export interface UpdateStoragePoolRequest {
  name?: string;
  description?: string;
  is_enabled?: boolean;
}

/**
 * Storage pools (ADR-013), managed from the console since ADR-050: admin and
 * sub_admin read, admin writes. Writes pass skipGlobalError so the page lands
 * 400 VALIDATION_ERROR `fields` on the form and 409 POOL_NAME_TAKEN /
 * POOL_DESTINATION_EXISTS on the right input (QA #68 / #69) instead of the
 * global snackbar.
 */
export const storagePoolService = {
  async listPools(): Promise<StoragePool[]> {
    const response = await api.get<ApiResponse<StoragePool[]>>('/api/v1/storage-pools');
    return response.data.data || [];
  },

  async createPool(data: CreateStoragePoolRequest): Promise<StoragePool> {
    const response = await api.post<ApiResponse<StoragePool>>('/api/v1/storage-pools', data, { skipGlobalError: true });
    return response.data.data!;
  },

  async updatePool(id: string, data: UpdateStoragePoolRequest): Promise<StoragePool> {
    const response = await api.put<ApiResponse<StoragePool>>(`/api/v1/storage-pools/${id}`, data, { skipGlobalError: true });
    return response.data.data!;
  },

  async deletePool(id: string): Promise<void> {
    await api.delete(`/api/v1/storage-pools/${id}`, { skipGlobalError: true });
  },

  /** Add an on-premise destination as a member (cloud rows answer 400 on destination_id). */
  async addDestination(poolId: string, destinationId: string, priority: number): Promise<void> {
    await api.post(
      `/api/v1/storage-pools/${poolId}/destinations`,
      { destination_id: destinationId, priority },
      { skipGlobalError: true },
    );
  },

  async removeDestination(poolId: string, destinationId: string): Promise<void> {
    await api.delete(`/api/v1/storage-pools/${poolId}/destinations/${destinationId}`, { skipGlobalError: true });
  },

  /**
   * The destinations a pool may hold (ADR-050 D-2): every on-premise
   * destination on the platform (the caller is admin, so the list is not
   * group-scoped). Cloud destinations are worker-environment projections and
   * cannot be members.
   */
  async listCandidateDestinations(): Promise<BackupDestination[]> {
    const response = await api.get<ApiResponse<{ data: BackupDestination[] }>>('/api/v1/backup-destinations', {
      params: { type: 'on_premise', limit: 100 },
    });
    return response.data.data?.data || [];
  },

  /**
   * The cloud destinations the worker is deployed with (ADR-041 projection).
   * They are not pool members in the database; the console shows them as the
   * built-in members of every 智慧分散池 (product decision 2026-09-29) and
   * says they cannot join a 備份池.
   */
  async listCloudDestinations(): Promise<BackupDestination[]> {
    const response = await api.get<ApiResponse<{ data: BackupDestination[] }>>('/api/v1/backup-destinations', {
      params: { type: 'cloud', limit: 100 },
    });
    return response.data.data?.data || [];
  },
};
